import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { useGetExtranetPendingChange } from '~/hooks/apis/extranet/useExtranetPendingChanges';
import useQuery from '~/hooks/useQuery';

import { ExtranetPendingChange, ListExtranetPendingChangesParams } from '~/services/ExtranetService';

import { getTargetIdValue } from '../changeMetadata';
import { DEFAULT_FILTERS, HISTORY_TAB } from '../constants';
import {
  buildFilterSearch,
  buildListParams,
  getDefaultPropertyCategoryForTab,
  getDefaultStatusForTab,
  getFiltersFromQuery,
  getQueryParam,
  getTabFromChangeStatus,
  getTabFromQuery,
} from '../filterState';
import type { Filters } from '../types';

export default function useExtranetApprovalsUrlState() {
  const query = useQuery();
  const navigate = useNavigate();
  const location = useLocation();
  const initialTab = getTabFromQuery(getQueryParam(query, 'tab'));
  const initialFilters = getFiltersFromQuery(query, initialTab);
  const deepLinkedChangeId = getQueryParam(query, 'changeId', 'change_id');
  const openedDeepLinkChangeIdRef = useRef<string | null>(null);
  const skipNextQuerySyncRef = useRef(false);

  const [activeTab, setActiveTab] = useState(initialTab);
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<ListExtranetPendingChangesParams>(
    buildListParams(initialFilters, 1),
  );
  const [page, setPage] = useState(1);
  const [deepLinkedDiffRow, setDeepLinkedDiffRow] = useState<ExtranetPendingChange | null>(null);

  const { data: deepLinkedChangeData } = useGetExtranetPendingChange(deepLinkedChangeId);

  const isPendingTab = activeTab !== HISTORY_TAB;
  const isHistoryTab = activeTab === HISTORY_TAB;

  const syncUrlFilters = useCallback(
    (nextFilters: Filters, nextTab: number, options: { changeId?: string; replace?: boolean } = {}) => {
      const search = buildFilterSearch(nextFilters, nextTab, options.changeId);
      if (search === location.search) {
        return;
      }

      skipNextQuerySyncRef.current = true;
      navigate({ pathname: location.pathname, search }, { replace: options.replace ?? true });
    },
    [location.pathname, location.search, navigate],
  );

  useEffect(() => {
    if (skipNextQuerySyncRef.current) {
      skipNextQuerySyncRef.current = false;
      return;
    }

    const nextTab = getTabFromQuery(getQueryParam(query, 'tab'));
    const nextFilters = getFiltersFromQuery(query, nextTab);
    setActiveTab(nextTab);
    setFilters(nextFilters);
    setPage(1);
    setAppliedFilters(buildListParams(nextFilters, 1));
  }, [query]);

  useEffect(() => {
    const deepLinkedChange = deepLinkedChangeData?.result;
    if (!deepLinkedChangeId || !deepLinkedChange || openedDeepLinkChangeIdRef.current === deepLinkedChangeId) {
      return;
    }

    const nextTab = getTabFromChangeStatus(deepLinkedChange.status);
    const nextFilters = {
      ...getFiltersFromQuery(query, nextTab),
      vendor_id: getQueryParam(query, 'vendor_id', 'vendorId') || deepLinkedChange.fk_vendor_id,
      property_id:
        getQueryParam(query, 'property_id', 'propertyId') ||
        getTargetIdValue(deepLinkedChange.target_ids, 'property_id') ||
        '',
    };

    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
      setFilters(nextFilters);
      setPage(1);
      setAppliedFilters(buildListParams(nextFilters, 1));
      syncUrlFilters(nextFilters, nextTab, { changeId: deepLinkedChangeId, replace: true });
    }

    setDeepLinkedDiffRow(deepLinkedChange);
    openedDeepLinkChangeIdRef.current = deepLinkedChangeId;
  }, [activeTab, deepLinkedChangeData, deepLinkedChangeId, query, syncUrlFilters]);

  const handleSearch = useCallback(() => {
    setPage(1);
    setAppliedFilters(buildListParams(filters, 1));
    syncUrlFilters(filters, activeTab);
  }, [activeTab, filters, syncUrlFilters]);

  const handleClear = useCallback(() => {
    const resetFilters = {
      ...DEFAULT_FILTERS,
      status: getDefaultStatusForTab(activeTab),
      property_category: getDefaultPropertyCategoryForTab(activeTab),
    };
    setFilters(resetFilters);
    setPage(1);
    setAppliedFilters(buildListParams(resetFilters, 1));
    syncUrlFilters(resetFilters, activeTab);
  }, [activeTab, syncUrlFilters]);

  const changeTab = useCallback(
    (nextTab: number) => {
      setActiveTab(nextTab);
      setPage(1);
      const resetFilters = {
        ...DEFAULT_FILTERS,
        status: getDefaultStatusForTab(nextTab),
        property_category: getDefaultPropertyCategoryForTab(nextTab),
      };
      setFilters(resetFilters);
      setAppliedFilters(buildListParams(resetFilters, 1));
      syncUrlFilters(resetFilters, nextTab);
    },
    [syncUrlFilters],
  );

  const changePage = useCallback((nextPage: number) => {
    setPage(nextPage);
    setAppliedFilters((prev) => ({ ...prev, page: nextPage }));
  }, []);

  const clearDeepLinkedDiffRow = useCallback(() => {
    setDeepLinkedDiffRow(null);
  }, []);

  const deepLinkedChange = useMemo(() => deepLinkedChangeData?.result, [deepLinkedChangeData]);

  return {
    activeTab,
    appliedFilters,
    changePage,
    changeTab,
    clearDeepLinkedDiffRow,
    deepLinkedChange,
    deepLinkedDiffRow,
    filters,
    handleClear,
    handleSearch,
    isHistoryTab,
    isPendingTab,
    page,
    setFilters,
  };
}
